import React, { useEffect, useState } from 'react'
import Text from '../Text'
import styles from './styles.module.css'
import { useRegionShippingInfo } from '../RegionGeo'
import { useRegionOffers } from '../RegionOffers'
import { $textSecondary, $textInteractive, $textPrimary, $textTertiary } from '../../styles/colors/semantics'
import {
  $black,
  $neutralGray100,
  $neutralGray20,
  $neutralGray30,
  $neutralGray70,
  $red60,
  $white
, $green50 } from '../../styles/colors/primitives'
import { buildCheckoutPageUrlFromProductSetId } from '../../utils/shopItems'
import { useUrlRegion } from '../../utils/useCurrentRegion'
import OptimizedImage from '../OptimizedImage'
import MyLoader from '../Utils/MyLoader'
import PreOrderTag from './PreOrderTag'
import EndOfLifeTag from './EndOfLifeTag'
import CardTag from './CardTag'
import { EXPO_PROMO, isExpoPromoLive, isExcludedFromExpoPromo } from '../../lib/expoPromo'
import SmartLink from '../SmartLink'
import { formatPrice } from '../../utils/formats'
import FreeShippingTag from './FreeShippingTag'
import Spacer from '../Spacer'
import { pushEcommerceEvent, toGA4Item } from '../../utils/dataLayer'
import SaleBonusPill from '../SaleCampaign/SaleBonusPill'
import DealCountdown from '../SaleCampaign/DealCountdown'
import DealPrice from '../SaleCampaign/DealPrice'

function ShopItem({ dark, isLoading, name, typeNames, freeBonuses, saleBonus = null, mainImage, hoverImage, regularPriceObj,
                    discountPriceObj, priceObj, price, discountPrice, outOfStock, outOfStockMessage, isNew, isPreOrder, hasSpecialOffer, productSetId, pricePrefix, priority = false,
                    colorVariants, currentColorName, region: regionProp = null, endOfLife = false, cardTag = null, cardTagVariant = null, cardTagUntil = null, itemType = null,
                    saleDeal = null }) {
  // Offers migrated off Overmind (Phase D1): the click-time item lookup for the
  // GA4 select_item event reads the offers from the RegionOffers seam.
  const { offers } = useRegionOffers()
  // Sim Expo 2026 pill: false on SSR + the hydration render, set after mount.
  const [expoLive, setExpoLive] = useState(false)
  useEffect(() => { setExpoLive(isExpoPromoLive()) }, [])
  // Catalog `cardTagUntil` (ISO time): the card tag hides itself from then on.
  // Checked at render (a fresh SSR after the end never shows it), again after
  // mount (a page cached from before the end drops it), and flipped by a timer
  // while a page stays open across the end.
  const cardTagUntilMs = cardTagUntil ? Date.parse(cardTagUntil) : NaN
  const [cardTagExpired, setCardTagExpired] = useState(() => Date.now() >= cardTagUntilMs)
  useEffect(() => {
    if (Number.isNaN(cardTagUntilMs)) return undefined
    const left = cardTagUntilMs - Date.now()
    if (left <= 0) { setCardTagExpired(true); return undefined }
    if (left > 2 ** 31 - 1) return undefined
    const t = setTimeout(() => setCardTagExpired(true), left)
    return () => clearTimeout(t)
  }, [cardTagUntilMs])
  // `region` (Overmind→server-props migration, slice 1): the page region is
  // precomputed server-side and threaded in as a prop so the checkout href is
  // built off the render path. useUrlRegion() (purely URL-derived, SSR-safe — NOT
  // an Overmind read) stays as the fallback for the client path / callers that
  // don't thread it; both yield the same value on a region page, so SSR === client
  // first render. Called unconditionally (Rules of Hooks); the prop just wins.
  const urlRegion = useUrlRegion()
  const region = regionProp != null ? regionProp : urlRegion
  const largeThumb = ['gt2-cockpit', 'gt2-cockpit-black', 'gt2-cockpit-black-gt2-seat', 'gt2-seat'].includes(productSetId)

  // taxIncluded = taxIncluded ? taxIncluded.replaceAll(/ *\([^)]*\) */g, '').trim() : null
  // shippingInfo migrated to the RegionGeo server-props seam (Phase C2 part 2).
  const shippingInfo = useRegionShippingInfo()
  const hasFreeShipping = shippingInfo && shippingInfo.freeShipping
  const hasDiscount = !!discountPrice && discountPrice !== price
  // running limited deal: the card switches to the campaign look (black, Kanit)
  const isDeal = !!saleDeal && !outOfStock
  const discountPercentage = hasDiscount && !!price && discountPriceObj && regularPriceObj && regularPriceObj.priceCents
    ? (100 - (discountPriceObj.priceCents / regularPriceObj.priceCents) * 100).toFixed(0)
    : false

  if (isLoading) {
    return (
      <div className={styles.loaderWrapper} style={{ background: dark ? $neutralGray100 : $white }}>
        <MyLoader isLoading={isLoading}/>
      </div>
    )
  }

  const onSelect = () => {
    const shopItem = (offers || []).find(i => i.productSetId === productSetId)
    if (!shopItem) return
    const ga4Item = toGA4Item(shopItem, 1)
    if (!ga4Item) return
    pushEcommerceEvent('select_item', {
      items: [ga4Item],
      item_list_id: 'shop',
      item_list_name: 'Shop',
    })
  }

  return (
    <SmartLink
      href={buildCheckoutPageUrlFromProductSetId(productSetId, region)}
      onClick={onSelect}
      className={[
        styles.wrapper,
        !hoverImage ? null : styles.wrapperHoverable,
        !hoverImage ? null : (largeThumb ? styles.wrapperHoverableLargeThumb : null),
        isDeal ? styles.wrapperDeal : null
      ].filter(Boolean).join(' ')}
      style={isDeal ? undefined : { background: dark ? $black : 'transparent', color: dark ? $textPrimary.dark : 'inherit' }}
    >
        <div
          className={[
            styles.imageWrapper,
            (outOfStock && !isNew) ? styles.imageWrapperDisabled : null,
            largeThumb ? styles.imageWrapperLargeThumb : null
          ].filter(Boolean).join(' ')}
          style={isDeal ? undefined : { background: dark ? $neutralGray100 : '#F4F4F4' }}
        >
          <div className={[styles.mainImage, largeThumb ? styles.mainImageLargeThumb : null].filter(Boolean).join(' ') + ' main-image'}>
            {/* mainImage/hoverImage are ResolvedImage descriptors (normalized
                server-side, task #67). LCP for the first shop row keeps priority. */}
            <OptimizedImage
              image={mainImage}
              alt={(mainImage && mainImage.alt) || name}
              sizes="(max-width: 600px) 100vw, (max-width: 767px) 50vw, 384px"
              priority={priority}
              reserveSpace={false}
            />
          </div>
          <div className={[styles.hoverImage, largeThumb ? styles.hoverImageLargeThumb : null].filter(Boolean).join(' ') + ' hover-image'}>
            {hoverImage && (
              <OptimizedImage
                image={hoverImage}
                alt={(hoverImage && hoverImage.alt) || name}
                sizes="(max-width: 600px) 100vw, (max-width: 767px) 50vw, 384px"
                reserveSpace={false}
              />
            )}
          </div>
        </div>
        <div className={styles.itemInfoWrapper}>
          {/* Stripe payment-method messaging is intentionally NOT rendered on
              the shop grid: one Stripe <Elements> per card meant ~2N cross-origin
              iframes on a 24-product page, which crashed mobile WebKit (renderer
              OOM) on country change. The messaging still renders on the product
              and checkout pages (single instance). */}
          {isDeal ? renderDealInfo({ name, typeNames, saleDeal, discountPriceObj, regularPriceObj }) : (<>
          <div className={styles.priceAndTaxWrapper}>
            <div className={styles.priceWrapper}>
              {pricePrefix && <Text type={'heading-7'}>{pricePrefix}</Text>}
              <div
                className={[styles.discountPrice, hasDiscount ? styles.shown : styles.hidden].join(' ')}
                style={{ background: dark ? $black : 'transparent', color: $textInteractive.light }}
              >
                <Text type="heading-7">{discountPrice}</Text>
              </div>
              <div className={styles.priceAndDiscountPercentage} style={{ background: dark ? $black : 'transparent' }}>
                <div
                  className={[styles.price, hasDiscount ? styles.priceHasDiscount : null].filter(Boolean).join(' ')}
                  style={hasDiscount ? { color: $textTertiary.light } : undefined}
                >
                  <Text type={hasDiscount ? 'small' : 'heading-7'}>{price}</Text>
                </div>
                {
                  discountPercentage > 0 && <div className={styles.discountPercentage} style={{ color: dark ? $textInteractive.light : 'transparent' }}>
                    <Text type="tiny">-{discountPercentage}%</Text>
                  </div>
                }
              </div>
            </div>
            {/*{*/}
            {/*  taxIncluded && <TaxIncluded show={!!taxIncluded}>*/}
            {/*    <Text type="tiny">Incl. {taxIncluded}</Text>*/}
            {/*  </TaxIncluded>*/}
            {/*}*/}
          </div>
          <div className={styles.title}>
            {/* product-name "G2" in VRS red (no-op for names without "G2") */}
            <Text type="medium" highlight="G2" highlightSx="color: #E11D48;" highlightExactMatch>{name}</Text>
            <Spacer size={4}/>
            { renderBonuses(freeBonuses) }
            { renderSaleBonus(saleBonus, dark) }
            { typeNames && typeNames.map(n => <Text type="small" key={n} color={dark ? $textSecondary.dark : $textSecondary.light}>{n}</Text>) }
          </div>
          </>)}
          {colorVariants && colorVariants.length > 1 && (
            <div className={styles.colorSwatchesRow}>
              {colorVariants.map(variant => (
                <div
                  key={variant.colorName}
                  className={styles.miniColorSwatch}
                  style={{
                    '--swatch-border': variant.colorName === currentColorName ? $red60 : $neutralGray20,
                    '--swatch-bg': $white,
                    '--swatch-after-bg': variant.colorHex,
                    '--swatch-after-border': $neutralGray20
                  }}
                />
              ))}
            </div>
          )}
          {/*<SaleOffer show={hasSpecialOffer && !outOfStock} onClick={() => actions.setShowGlobalSaleOffer({ show: true })}>*/}
          {/*  <Text type="tiny">Special offer</Text>*/}
          {/*  <InfoIcon/>*/}
          {/*</SaleOffer>*/}
          <div
            className={[styles.outOfStockTag, outOfStock ? styles.shown : styles.hidden].join(' ')}
            style={{ background: $neutralGray30, color: $neutralGray70 }}
          >
            <Text type="tiny">{outOfStockMessage}</Text>
          </div>
          <FreeShippingTag show={hasFreeShipping}/>
          <PreOrderTag show={isPreOrder}/>
          {/* only while the item can still be bought - a sold-out card already
              carries the out-of-stock pill. */}
          <EndOfLifeTag show={!!endOfLife && !outOfStock}/>
          {/* catalog `cardTag` (e.g. "Complete setup" on the full-rig bundle).
              Shares the top-left slot with the retirement pill; a retiring product
              keeps its "legend retires" line and the generic tag stays off. */}
          {/* While the Sim Expo promo runs, the expo pill is the ONLY promo pill:
              the catalog pill (e.g. "Sale - until 31 October", "Complete setup")
              is hidden and comes back when the promo ends. A running sale deal
              (saleDeal) brings its own pill and end time, so cardTagUntil does
              not apply to it. */}
          <CardTag
            label={cardTag}
            sub={saleDeal && saleDeal.sub}
            variant={cardTagVariant}
            className={isDeal ? styles.dealTag : null}
            show={!!cardTag && (isDeal || !cardTagExpired) && !endOfLife && !outOfStock && !expoLive}
          />
          {/* Sim Expo 2026: every buyable card says the 15% is applied at
              checkout. Sits under the retirement pill when one is shown.
              Mount-gated (expoLive is false on SSR + hydration) so a page cached
              across the start/end moment never mismatches. */}
          <CardTag
            label={isExcludedFromExpoPromo(itemType) ? EXPO_PROMO.excludedCardTag : EXPO_PROMO.cardTag}
            variant={isExcludedFromExpoPromo(itemType) ? 'muted' : 'fire'}
            show={expoLive && !outOfStock}
            offsetTop={endOfLife ? 34 : 0}
          />
        </div>
    </SmartLink>
  )
}

// Seasonal free extra (lib/saleCampaign getSaleBonus), e.g. 3 months of VRS
// Competitive. Added value next to the price, never part of it.
const renderSaleBonus = (saleBonus, dark) => {
  if (!saleBonus || !saleBonus.label) return null
  return (
    <div className={styles.saleBonus}>
      <SaleBonusPill bonus={saleBonus} theme={dark ? 'dark' : 'light'}/>
    </div>
  )
}

// Running limited deal (lib/saleCampaign getSaleCardTag): name in the campaign
// heading style, deal price with % off and "You save", countdown to the deal
// end. The clock comes from the Shop island (one timer for the whole grid);
// `mounted=false` renders placeholders so SSR never bakes in a stale count.
const renderDealInfo = ({ name, typeNames, saleDeal, discountPriceObj, regularPriceObj }) => (
  <div className={styles.dealInfo}>
    <h3 className={styles.dealName}>{name}</h3>
    <DealPrice price={discountPriceObj || regularPriceObj} regular={regularPriceObj}/>
    {saleDeal.endsAtMs && (
      <DealCountdown className={styles.saleDeal} targetMs={saleDeal.endsAtMs} nowMs={saleDeal.nowMs} mounted={saleDeal.mounted} live/>
    )}
    {typeNames && typeNames.length > 1 && <p className={styles.dealIncludes}>{typeNames.join(' + ')}</p>}
  </div>
)

const renderBonuses = (freeBonuses) => {
  if(!freeBonuses || Object.keys(freeBonuses).length === 0) return null
  return (
    <div className={styles.bonuses}>
      { freeBonuses && Object.keys(freeBonuses).map(k => (
        <React.Fragment key={k}>
          <Text type="small"
          highlight={'FREE'}
          highlightSx={`color: ${$green50}; font-weight: 500;`}
          color={$textSecondary.light}
          >+ { k } for <span className={styles.lineThroughSpan}>{formatPrice(freeBonuses[k])}</span> FREE</Text>
        </React.Fragment>
      ))
      }
    </div>
  )
}

export default ShopItem
